/**
 * Section A4 Page
 * A.4.專案位置
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import sectionA4Service from '../services/sectionA4Service';
import { getSystemCodesByType, SystemCode } from '../services/systemCodeService';
import { SectionA4 } from '../types/section_a4';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';
import '../styles/DataTable.css';

// Fix Leaflet default marker icon issue
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Map update component to fly to new coordinates
const MapUpdater: React.FC<{ lat: number; lng: number }> = ({ lat, lng }) => {
  const map = useMap();
  useEffect(() => {
    map.flyTo([lat, lng], 13);
  }, [lat, lng, map]);
  return null;
};

// Map click handler component
const MapClickHandler: React.FC<{ onLocationSelect: (lat: number, lng: number) => void }> = ({ onLocationSelect }) => {
  const map = useMap();

  useEffect(() => {
    const handleClick = (e: L.LeafletMouseEvent) => {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    };

    map.on('click', handleClick);

    return () => {
      map.off('click', handleClick);
    };
  }, [map, onLocationSelect]);

  return null;
};

const SectionA4Page: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { selectedProjectId, selectedProject } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);

  // ========== 資料狀態 ==========
  const [sectionA4Data, setSectionA4Data] = useState<SectionA4 | null>(null);
  const [hostPartyOptions, setHostPartyOptions] = useState<SystemCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // ========== 表單狀態 ==========
  const emptyForm = {
    host_party: 0,
    region: '',
    state: '',
    province: '',
    cities: '',
    towns: '',
    communities: '',
    latitude: '',
    longitude: ''
  };

  const [formData, setFormData] = useState(emptyForm);
  const [initialFormData, setInitialFormData] = useState(emptyForm);

  // ========== 初始化：載入主辦國選項 ==========
  useEffect(() => {
    if (!hasInitialized.current && !permissionLoading) {
      validateSession('SectionA4Page');
      loadHostPartyOptions();
      hasInitialized.current = true;
    }
  }, [permissionLoading]);

  const loadHostPartyOptions = async () => {
    try {
      const codes = await getSystemCodesByType('Article 6.4 Party', undefined, true);
      setHostPartyOptions(codes);
    } catch (err: any) {
      console.error('載入主辦國選項失敗:', err);
    }
  };

  // ========== 取得使用者位置 ==========
  const getUserLocation = (): Promise<{ latitude: string; longitude: string }> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('瀏覽器不支援定位功能'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude.toFixed(6),
            longitude: position.coords.longitude.toFixed(6)
          });
        },
        (error) => {
          console.warn('無法取得位置:', error.message);
          reject(error);
        },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
      );
    });
  };

  // ========== 專案變更時載入資料 ==========
  useEffect(() => {
    if (!selectedProjectId) {
      setSectionA4Data(null);
      setFormData(emptyForm);
      setInitialFormData(emptyForm);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        setSuccess(null);
        const data = await sectionA4Service.getSectionA4ByProject(selectedProjectId);

        if (data) {
          setSectionA4Data(data);
          const loadedData = {
            host_party: data.host_party || selectedProject?.host_party || 0,
            region: data.region || '',
            state: data.state || '',
            province: data.province || '',
            cities: data.cities || '',
            towns: data.towns || '',
            communities: data.communities || '',
            latitude: data.latitude?.toString() || '',
            longitude: data.longitude?.toString() || ''
          };
          setFormData(loadedData);
          setInitialFormData(loadedData);
        } else {
          let defaultLocation = { latitude: '40.749444', longitude: '-73.968056' };
          try {
            defaultLocation = await getUserLocation();
          } catch {
            console.log('未能取得使用者位置，使用聯合國總部位置作為預設值');
          }

          const newData = {
            host_party: selectedProject?.host_party || 0,
            region: '',
            state: '',
            province: '',
            cities: '',
            towns: '',
            communities: '',
            latitude: defaultLocation.latitude,
            longitude: defaultLocation.longitude
          };
          setSectionA4Data(null);
          setFormData(newData);
          setInitialFormData(newData);
        }
      } catch (err: any) {
        console.error('載入 A.4 資料失敗:', err);
        setError(err.response?.data?.detail || '載入資料失敗');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedProjectId, selectedProject]);

  // ========== 儲存 ==========
  const handleSave = async () => {
    if (!selectedProjectId) {
      toast.warning(i18n.language === 'zh-TW' ? '請先在上方選擇專案' : 'Please select a project first');
      return;
    }

    const lat = formData.latitude ? parseFloat(formData.latitude) : null;
    const lng = formData.longitude ? parseFloat(formData.longitude) : null;

    if (lat !== null && (lat < -90 || lat > 90)) {
      toast.warning(i18n.language === 'zh-TW' ? '緯度必須在 -90 到 90 之間' : 'Latitude must be between -90 and 90');
      return;
    }

    if (lng !== null && (lng < -180 || lng > 180)) {
      toast.warning(i18n.language === 'zh-TW' ? '經度必須在 -180 到 180 之間' : 'Longitude must be between -180 and 180');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const updatedData = await sectionA4Service.updateSectionA4(selectedProjectId, {
        region: formData.region || undefined,
        state: formData.state || undefined,
        province: formData.province || undefined,
        cities: formData.cities || undefined,
        towns: formData.towns || undefined,
        communities: formData.communities || undefined,
        latitude: lat !== null ? lat : undefined,
        longitude: lng !== null ? lng : undefined
      });

      setSectionA4Data(updatedData);
      const savedData = {
        ...formData,
        latitude: updatedData.latitude?.toString() || '',
        longitude: updatedData.longitude?.toString() || ''
      };
      setFormData(savedData);
      setInitialFormData(savedData);
      setSuccess(i18n.language === 'zh-TW' ? '儲存成功' : 'Saved successfully');
      toast.success(i18n.language === 'zh-TW' ? '儲存成功' : 'Saved successfully');

    } catch (err: any) {
      console.error('儲存失敗:', err);
      const errorMsg = err.response?.data?.detail || '儲存失敗';
      setError(errorMsg);
      toast.error(i18n.language === 'zh-TW' ? `儲存失敗: ${errorMsg}` : `Save failed: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  // ========== 重置 ==========
  const handleReset = async () => {
    const ok = await toast.confirm({
      title: i18n.language === 'zh-TW' ? '操作確認' : 'Confirmation',
      message: i18n.language === 'zh-TW' ? '確定要重置所有欄位嗎？' : 'Are you sure you want to reset all fields?',
      confirmText: i18n.language === 'zh-TW' ? '重設' : 'Reset',
    });
    if (ok) {
      setFormData(initialFormData);
    }
  };

  // ========== 取得目前位置 ==========
  const handleGetCurrentLocation = async () => {
    try {
      setLoading(true);
      const location = await getUserLocation();
      setFormData(prev => ({ ...prev, latitude: location.latitude, longitude: location.longitude }));
      toast.success(i18n.language === 'zh-TW' ? '已取得目前位置' : 'Current location obtained');
    } catch {
      toast.error(i18n.language === 'zh-TW' ? '無法取得位置，請確認已允許瀏覽器存取位置' : 'Unable to get location. Please allow browser location access');
    } finally {
      setLoading(false);
    }
  };

  // ========== 地圖點擊處理 ==========
  const handleMapClick = (lat: number, lng: number) => {
    if (canEdit) {
      // 正規化經度到 -180~180（Leaflet 世界複製圖層會回傳 >180 或 <-180 的值）
      const normalizedLng = ((lng % 360) + 540) % 360 - 180;
      setFormData(prev => ({ ...prev, latitude: lat.toFixed(6), longitude: normalizedLng.toFixed(6) }));
    }
  };

  // ========== 權限檢查 ==========
  if (permissionLoading) {
    return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  }

  if (!hasPermission('section_a4', 'read')) {
    return (
      <div className="page-container">
        <div className="error-message">{t('common.noPermission')}</div>
      </div>
    );
  }

  const canEdit = hasPermission('section_a4', 'update');

  // ========== 渲染 ==========
  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode="section_a4" />
      </div>

      {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}
      {success && <div className="success-message" style={{ marginBottom: '16px' }}>{success}</div>}

      {selectedProjectId && (
        <div style={{ marginBottom: '24px' }}>
          {loading ? (
            <div className="loading">{t('common.loading')}</div>
          ) : (
            <>
              {/* 主辦國（鎖定，不可編輯） */}
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#666' }}>
                  {i18n.language === 'zh-TW' ? '主辦國 (鎖定，不可變更)' : 'Host Party (Locked)'}
                </label>
                <select
                  value={formData.host_party || ''}
                  disabled
                  style={{ width: '100%', maxWidth: '600px', padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: '4px', backgroundColor: '#e8e8e8', cursor: 'not-allowed', color: '#333' }}
                >
                  <option value="">{i18n.language === 'zh-TW' ? '未設定' : 'Not set'}</option>
                  {hostPartyOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {i18n.language === 'zh-TW' ? option.code_cname : (option.code_ename || option.code_cname)}
                    </option>
                  ))}
                </select>
                <small style={{ color: '#888', fontSize: '0.85em' }}>
                  {i18n.language === 'zh-TW' ? '主辦國資訊來自專案基本資料設定，無法在此修改' : 'Host party is locked from project settings'}
                </small>
              </div>

              {/* 地理位置資訊 (2x3 網格) */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '16px' }}>
                {[
                  { key: 'region', labelZh: '郡', labelEn: 'Region' },
                  { key: 'state', labelZh: '州', labelEn: 'State' },
                  { key: 'province', labelZh: '省', labelEn: 'Province' },
                  { key: 'cities', labelZh: '城市', labelEn: 'Cities' },
                  { key: 'towns', labelZh: '城鎮', labelEn: 'Towns' },
                  { key: 'communities', labelZh: '社區', labelEn: 'Communities' },
                ].map(({ key, labelZh, labelEn }) => (
                  <div key={key}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                      {i18n.language === 'zh-TW' ? labelZh : labelEn}
                    </label>
                    <input
                      type="text"
                      value={(formData as any)[key]}
                      onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                      disabled={!canEdit}
                      maxLength={200}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: '4px' }}
                    />
                  </div>
                ))}
              </div>

              {/* 經緯度 */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ fontWeight: 600, margin: 0 }}>
                    {i18n.language === 'zh-TW' ? '經緯度座標' : 'Coordinates'}
                  </label>
                  {canEdit && (
                    <button className="btn-secondary" onClick={handleGetCurrentLocation} disabled={loading} style={{ padding: '6px 12px', fontSize: '0.9em' }}>
                      📍 {i18n.language === 'zh-TW' ? '取得目前位置' : 'Get Current Location'}
                    </button>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.9em', color: '#666' }}>
                      {i18n.language === 'zh-TW' ? '緯度 (Latitude)' : 'Latitude'}
                    </label>
                    <input
                      type="number" step="0.000001" value={formData.latitude}
                      onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                      disabled={!canEdit} placeholder="-90 to 90"
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: '4px' }}
                    />
                    <small style={{ color: '#999', fontSize: '0.85em' }}>{i18n.language === 'zh-TW' ? '範圍: -90 到 90' : 'Range: -90 to 90'}</small>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.9em', color: '#666' }}>
                      {i18n.language === 'zh-TW' ? '經度 (Longitude)' : 'Longitude'}
                    </label>
                    <input
                      type="number" step="0.000001" value={formData.longitude}
                      onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                      disabled={!canEdit} placeholder="-180 to 180"
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: '4px' }}
                    />
                    <small style={{ color: '#999', fontSize: '0.85em' }}>{i18n.language === 'zh-TW' ? '範圍: -180 到 180' : 'Range: -180 to 180'}</small>
                  </div>
                </div>
              </div>

              {/* OpenStreetMap 地圖 */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ fontWeight: 600, margin: 0 }}>
                    {i18n.language === 'zh-TW' ? '地圖位置' : 'Map Location'}
                  </label>
                  {canEdit && (
                    <small style={{ color: '#4A90E2', fontSize: '0.85em' }}>
                      💡 {i18n.language === 'zh-TW' ? '提示：點擊地圖可設定位置' : 'Tip: Click anywhere on the map to set location'}
                    </small>
                  )}
                </div>
                <div style={{ width: '100%', height: '400px', border: '1px solid #d9d9d9', borderRadius: '4px', overflow: 'hidden', cursor: canEdit ? 'crosshair' : 'default' }}>
                  {(() => {
                    const hasCoords = formData.latitude && formData.longitude &&
                      !isNaN(parseFloat(formData.latitude)) && !isNaN(parseFloat(formData.longitude));
                    const lat = hasCoords ? parseFloat(formData.latitude) : 23.5;
                    const lng = hasCoords ? parseFloat(formData.longitude) : 121.0;
                    const zoom = hasCoords ? 13 : 3;
                    return (
                      <MapContainer center={[lat, lng]} zoom={zoom} style={{ width: '100%', height: '100%' }} worldCopyJump={false}>
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        {hasCoords && (
                          <>
                            <Marker position={[lat, lng]}>
                              <Popup>
                                {i18n.language === 'zh-TW' ? '專案位置' : 'Project Location'}<br />
                                <small>{lat.toFixed(6)}, {lng.toFixed(6)}</small>
                              </Popup>
                            </Marker>
                            <MapUpdater lat={lat} lng={lng} />
                          </>
                        )}
                        {canEdit && <MapClickHandler onLocationSelect={handleMapClick} />}
                      </MapContainer>
                    );
                  })()}
                </div>
              </div>

              {canEdit && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                  <button className="btn-primary" onClick={handleSave} disabled={loading}>
                    {loading ? (i18n.language === 'zh-TW' ? '儲存中...' : 'Saving...') : (i18n.language === 'zh-TW' ? '儲存' : 'Save')}
                  </button>
                  <button className="btn-secondary" onClick={handleReset} disabled={loading}>
                    {i18n.language === 'zh-TW' ? '重置' : 'Reset'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!selectedProjectId && !loading && (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: '16px' }}>
          {i18n.language === 'zh-TW' ? '請先在上方選擇專案' : 'Please select a project from the top bar'}
        </div>
      )}
    </div>
  );
};

export default SectionA4Page;
